import React, { useState, useEffect, useRef } from 'react';

import AccidentsImg from '../assets/Danger/Accidents & Injuries.avif';
import AcidImg from '../assets/Danger/Acid.avif';
import CancerImg from '../assets/Danger/Cancer.avif';
import ChronicPainImg from '../assets/Danger/Chronic Pain.jpg';
import DepressionImg from '../assets/Danger/Depression & Anxiety.avif';
import HeartAttackImg from '../assets/Danger/Heart Attack or Stroke.avif';
import ObesityImg from '../assets/Danger/Obesity.avif';
import DiabetesImg from '../assets/Danger/Type 2 Diabetes.jpg';

const dangers = [
  {
    image: HeartAttackImg,
    title: 'Heart Attack or Stroke',
    description:
      'Repeated oxygen deprivation during sleep raises blood pressure and strains the cardiovascular system — significantly increasing the risk of heart attack and stroke over time.',
    fact: 'Up to 2–4× higher cardiovascular risk',
  },
  {
    image: DiabetesImg,
    title: 'Type 2 Diabetes',
    description:
      'Sleep apnea disrupts glucose metabolism and promotes insulin resistance. Poor sleep quality is a recognised independent risk factor for developing type 2 diabetes.',
    fact: '30% of sleep apnea patients have diabetes',
  },
  {
    image: DepressionImg,
    title: 'Depression & Anxiety',
    description:
      'Chronic sleep disruption impairs emotional regulation and depletes serotonin levels. Studies show a strong bidirectional relationship between untreated sleep apnea and mental health disorders.',
    fact: '5× more likely to develop depression',
  },
  {
    image: AccidentsImg,
    title: 'Accidents & Injuries',
    description:
      'Daytime sleepiness from poor sleep quality dramatically impairs reaction time and judgement. People with untreated sleep apnea are significantly more prone to workplace accidents and road collisions.',
    fact: '2.5× higher risk of motor accidents',
  },
  {
    image: ChronicPainImg,
    title: 'Chronic Pain',
    description:
      'Poor sleep interrupts the body\'s natural tissue repair cycle. Untreated sleep apnea lowers the pain threshold, worsens conditions like fibromyalgia, and is strongly linked to TMJ disorders.',
    fact: 'Directly linked to TMJ & jaw pain',
  },
  {
    image: ObesityImg,
    title: 'Obesity',
    description:
      'Sleep apnea disrupts ghrelin and leptin — hormones that regulate hunger and fullness. This creates a cycle: poor sleep drives cravings, weight gain worsens the airway, which deepens sleep apnea.',
    fact: 'Sleep & weight gain form a vicious cycle',
  },
  {
    image: AcidImg,
    title: 'Acid Reflux (GERD)',
    description:
      'The negative chest pressure during breathing pauses can pull stomach acid into the oesophagus. Sleep apnea and GERD co-occur in up to 60% of patients and each aggravates the other.',
    fact: 'Co-occurs in ~60% of sleep apnea patients',
  },
  {
    image: CancerImg,
    title: 'Cancer Risk',
    description:
      'Intermittent hypoxia (low oxygen) from apnea events promotes tumour growth and suppresses immune surveillance. Research links severe sleep apnea to elevated risk of several cancers.',
    fact: 'Hypoxia linked to accelerated tumour growth',
  },
];

const AUTOPLAY_DELAY = 5000;

export default function DangersCarousel() {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);

  const goTo = (index) => {
    setCurrent((index + dangers.length) % dangers.length);
    setProgress(0);
    startTimeRef.current = Date.now();
  };

  const next = () => goTo(current + 1);
  const prev = () => goTo(current - 1);

  useEffect(() => {
    startTimeRef.current = Date.now();
    setProgress(0);

    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % dangers.length);
      startTimeRef.current = Date.now();
      setProgress(0);
    }, AUTOPLAY_DELAY);

    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setProgress(Math.min((elapsed / AUTOPLAY_DELAY) * 100, 100));
    }, 50);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(progressRef.current);
    };
  }, [current]);

  const slide = dangers[current];

  return (
    <section className="py-24 px-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4">
            Know the Risks
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
            The Dangers of Untreated Snoring &amp; Sleep Apnea
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Left untreated, sleep apnea goes far beyond snoring — it silently
            damages your health in serious ways.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden">

          {/* Progress bar */}
          <div className="h-1 bg-gray-100">
            <div
              className="h-1 bg-primary transition-none"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Slide content */}
          <div className="flex flex-col md:flex-row items-center gap-8 p-8 md:p-12 min-h-[320px]">

            {/* Image */}
            <div className="w-full md:w-56 h-48 md:h-56 rounded-2xl overflow-hidden flex-shrink-0 bg-primary/5">
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Text */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left gap-4 flex-1">
              <h3 className="text-2xl font-bold text-secondary">
                {slide.title}
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                {slide.description}
              </p>
              {/* Fact badge */}
              <div className="inline-block px-5 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                {slide.fact}
              </div>
            </div>

          </div>

          {/* Navigation */}
          <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-between">

            {/* Prev */}
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-secondary hover:border-secondary transition-colors"
              aria-label="Previous"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {dangers.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === current
                      ? 'bg-primary w-6'
                      : 'bg-gray-300 hover:bg-gray-400 w-2'
                  }`}
                />
              ))}
            </div>

            {/* Next */}
            <button
              onClick={next}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-secondary hover:border-secondary transition-colors"
              aria-label="Next"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

          </div>
        </div>

        {/* Slide counter */}
        <p className="text-center text-sm text-gray-400 mt-4">
          {current + 1} / {dangers.length}
        </p>

      </div>
    </section>
  );
}